// The universal Bill Pay flow (spec §6.2): category → biller → parameters →
// Fetch/Validate/See plans/Book/Continue → amount rules → Pay (MPIN+OTP) →
// receipt → save biller. Live param validation, never-silent fetch failure.
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import { CATEGORIES, billersForCategory, convenienceFeeRupees } from '@/mock/billPayCatalogue';
import { fetchBill, pay, saveBiller } from '@/api/billPay';
import { runBankClock } from '@/mock/phase4';
import type { BillFetch, Biller } from '@/types/phase4.types';
import { usePaymentAuth } from '@/pages/Pay/components/usePaymentAuth';
import PaymentAuthSheets from '@/pages/Pay/components/PaymentAuthSheets';
import { formatIndianCurrency } from '@/utils/currency';
import '../phase4.css';

type Step = 'biller' | 'params' | 'review' | 'receipt';

const validateParam = (kind: string, value: string): boolean => {
  if (!value.trim()) return false;
  if (kind === 'mobile') return /^[6-9]\d{9}$/.test(value);
  if (kind === 'dob') return /^\d{2}\/\d{2}\/\d{4}$/.test(value);
  return true;
};

const BillPayFlow = () => {
  const { categoryId } = useParams<{ categoryId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const category = CATEGORIES.find((c) => c.id === categoryId);

  const [step, setStep] = useState<Step>('biller');
  const [biller, setBiller] = useState<Biller | null>(null);
  const [params, setParams] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [fetch, setFetch] = useState<BillFetch | null>(null);
  const [fetching, setFetching] = useState(false);
  const [fetchError, setFetchError] = useState('');
  const [amount, setAmount] = useState('');
  const [nickname, setNickname] = useState('');
  const [receipt, setReceipt] = useState<import('@/types/phase4.types').BillPayment | null>(null);
  const [stateFilter, setStateFilter] = useState('');

  useEffect(() => {
    runBankClock();
  }, []);

  const billers = useMemo(() => billersForCategory(categoryId ?? ''), [categoryId]);
  const states = useMemo(() => [...new Set(billers.map((b) => b.state).filter(Boolean))] as string[], [billers]);
  const visibleBillers = stateFilter ? billers.filter((b) => b.state === stateFilter) : billers;

  // pre-select a biller coming from a saved-biller chip
  useEffect(() => {
    const savedId = (location.state as { savedBillerId?: string } | null)?.savedBillerId;
    if (savedId && billers.length > 0) {
      setBiller(billers[0]);
      setStep('params');
    }
  }, [location.state, billers]);

  const paramValid = biller ? biller.params.every((p) => validateParam(p.kind, params[p.key] ?? '')) : false;

  const doFetch = async (b: Biller) => {
    setFetching(true);
    setFetchError('');
    try {
      const result = await fetchBill(b.id, params);
      setFetch(result);
      if (result.amountRupees != null) setAmount(String(result.amountRupees));
      setStep('review');
    } catch (err) {
      setFetchError(err instanceof Error ? err.message : "Couldn't fetch bill. Try again");
    } finally {
      setFetching(false);
    }
  };

  const ctaLabel = (b: Biller): string =>
    b.mode === 'FETCH' ? 'Fetch bill'
    : b.mode === 'VALIDATE' ? 'Validate'
    : b.mode === 'PLAN' ? 'See plans'
    : b.mode === 'BOOKING' ? 'Book cylinder'
    : 'Continue';

  // ---- payment auth chain (MPIN → OTP), then pay ----
  const auth = usePaymentAuth(async () => {
    if (!biller) return;
    try {
      const payment = await pay({
        billerId: biller.id,
        params,
        amountRupees: Number(amount),
        nickname: nickname.trim() || undefined,
      });
      setReceipt(payment);
      setStep('receipt');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Payment failed.');
    }
  });

  const startPay = async () => {
    const fee = biller ? convenienceFeeRupees(biller.id) : 0;
    const total = Number(amount) + fee;
    const err = await auth.start('NOVABANK', `Pay ${biller?.name} ${formatIndianCurrency(total)}`);
    if (err) toast.error(err);
  };

  if (!category) return null;

  const fee = biller ? convenienceFeeRupees(biller.id) : 0;

  return (
    <div className="nova-p4-page">
      <div className="nova-p4-header">
        <button
          type="button"
          className="nova-icon-btn"
          title="Back"
          aria-label="Back"
          onClick={() => (step === 'biller'
            ? navigate((location.state as { backTo?: string } | null)?.backTo ?? '/pay/bills')
            : setStep(step === 'params' ? 'biller' : 'params'))}
        >
          <FontAwesomeIcon icon={faArrowLeft} />
        </button>
        <h2>{category.label}</h2>
        <span className="nova-p4-bc-badge">Bharat Connect</span>
      </div>

      {/* Step 1 — pick a biller */}
      {step === 'biller' && (
        <>
          {states.length > 0 && (
            <div className="nova-p4-chip-row">
              <button type="button" className={`nova-p4-chip ${!stateFilter ? 'active' : ''}`} onClick={() => setStateFilter('')}>All</button>
              {states.map((s) => (
                <button key={s} type="button" className={`nova-p4-chip ${stateFilter === s ? 'active' : ''}`} onClick={() => setStateFilter(s)}>{s}</button>
              ))}
            </div>
          )}
          {visibleBillers.map((b) => (
            <button key={b.id} type="button" className="nova-p4-row" onClick={() => { setBiller(b); setStep('params'); }}>
              <span className="nova-p4-row-text">
                <span className="nova-p4-row-title">{b.name}</span>
                <span className="nova-p4-row-sub">{b.mode === 'FETCH' ? 'Fetch & pay' : b.mode === 'VALIDATE' ? 'Validate & pay' : b.mode === 'PLAN' ? 'Prepaid plans' : b.mode === 'BOOKING' ? 'Book a cylinder' : 'Pay any amount'}</span>
              </span>
            </button>
          ))}
        </>
      )}

      {/* Step 2 — parameters with live validation */}
      {step === 'params' && biller && (
        <div className="nova-p4-card">
          <h3>{biller.name}</h3>
          {biller.params.map((p) => {
            const value = params[p.key] ?? '';
            const bad = touched[p.key] && !validateParam(p.kind, value);
            return (
              <div className="nova-p4-field" key={p.key}>
                <label htmlFor={`p_${p.key}`}>{p.label}</label>
                <input
                  id={`p_${p.key}`}
                  className="form-control"
                  value={value}
                  placeholder={p.hint}
                  onChange={(e) => setParams((prev) => ({ ...prev, [p.key]: e.target.value }))}
                  onBlur={() => setTouched((prev) => ({ ...prev, [p.key]: true }))}
                />
                {bad ? <div className="err">Enter a valid {p.label.toLowerCase()} ({p.hint}).</div> : <div className="hint">{p.hint}</div>}
              </div>
            );
          })}
          {fetchError && <div className="nova-p4-banner red">{fetchError}</div>}
          <button type="button" className="nova-p4-primary" disabled={!paramValid || fetching} onClick={() => void doFetch(biller)}>
            {fetching ? 'Please wait…' : ctaLabel(biller)}
          </button>
        </div>
      )}

      {/* Step 3 — review + amount rules + fee line */}
      {step === 'review' && biller && fetch && (
        <div className="nova-p4-card">
          <h3>{biller.name}</h3>
          <div className="nova-p4-moneyline muted"><span>Customer</span><span>{fetch.customerName}</span></div>
          {fetch.billDate && <div className="nova-p4-moneyline muted"><span>Bill date</span><span>{fetch.billDate}</span></div>}
          {fetch.dueDate && (
            <div className="nova-p4-moneyline muted">
              <span>Due date</span>
              <span style={fetch.overdue ? { color: '#c0392b', fontWeight: 700 } : undefined}>{fetch.dueDate}</span>
            </div>
          )}
          {fetch.info && <div className="nova-p4-banner blue" style={{ marginTop: 8 }}>{fetch.info}</div>}

          {fetch.plans ? (
            <>
              <div className="nova-p4-field" style={{ marginTop: 12 }}>
                <label>Choose a plan</label>
                {fetch.plans.map((p) => (
                  <button key={p.id} type="button" className="nova-p4-row" onClick={() => setAmount(String(p.amountRupees))}>
                    <span className="nova-p4-row-text">
                      <span className="nova-p4-row-title">{p.label} — {formatIndianCurrency(p.amountRupees)}</span>
                      <span className="nova-p4-row-sub">{p.detail}</span>
                    </span>
                    {amount === String(p.amountRupees) && <span className="nova-p4-status ok">Selected</span>}
                  </button>
                ))}
              </div>
            </>
          ) : (
            <div className="nova-p4-field" style={{ marginTop: 12 }}>
              <label htmlFor="amt">Amount</label>
              <input
                id="amt"
                className="form-control"
                type="number"
                min={biller.minAmountRupees ?? 1}
                value={amount}
                readOnly={biller.exactAmount === true}
                onChange={(e) => setAmount(e.target.value)}
              />
              {biller.minAmountRupees != null && Number(amount) < biller.minAmountRupees && (
                <div className="err">{category.label} recharge is {formatIndianCurrency(biller.minAmountRupees)} or more.</div>
              )}
              {biller.exactAmount === true && <div className="hint">This biller takes the exact bill amount only.</div>}
            </div>
          )}

          <div className="nova-p4-moneyline muted"><span>Convenience fee</span><span>{formatIndianCurrency(fee)}</span></div>
          <div className="nova-p4-moneyline total"><span>Total</span><span>{formatIndianCurrency(Number(amount || 0) + fee)}</span></div>

          <button
            type="button"
            className="nova-p4-primary"
            style={{ marginTop: 14 }}
            disabled={!amount || Number(amount) <= 0 || (biller.minAmountRupees != null && Number(amount) < biller.minAmountRupees)}
            onClick={() => void startPay()}
          >
            Pay {formatIndianCurrency(Number(amount || 0) + fee)}
          </button>
        </div>
      )}

      {/* Step 4 — receipt + save biller */}
      {step === 'receipt' && receipt && (
        <div className="nova-p4-card">
          <div style={{ textAlign: 'center', marginBottom: 14 }}>
            <span className={`nova-p4-status ${receipt.status === 'SUCCESS' ? 'ok' : receipt.status === 'PENDING' ? 'warn' : 'bad'}`}>
              {receipt.status === 'PENDING' ? 'Awaiting biller confirmation' : receipt.status === 'FAILED' ? 'Refund initiated' : 'Success'}
            </span>
          </div>
          <div className="nova-p4-moneyline total" style={{ border: 'none' }}><span>Amount</span><span>{formatIndianCurrency(receipt.amountRupees)}</span></div>
          <div className="nova-p4-moneyline muted"><span>Biller</span><span>{receipt.billerName}</span></div>
          <div className="nova-p4-moneyline muted"><span>Convenience fee</span><span>{formatIndianCurrency(receipt.feeRupees)}</span></div>
          <div className="nova-p4-moneyline muted"><span>Bharat Connect Txn ID</span><span>{receipt.bcId}</span></div>
          <div className="nova-p4-moneyline muted"><span>Date &amp; time</span><span>{new Date(receipt.at).toLocaleString('en-IN')}</span></div>
          {Object.entries(receipt.params).map(([k, v]) => (
            <div className="nova-p4-moneyline muted" key={k}><span>{k}</span><span>{v}</span></div>
          ))}

          {!receipt.nickname && (
            <div className="nova-p4-field" style={{ marginTop: 14 }}>
              <label htmlFor="nick">Save this biller (optional nickname)</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <input id="nick" className="form-control" maxLength={24} value={nickname} onChange={(e) => setNickname(e.target.value)} placeholder="e.g. Home electricity" />
                <button
                  type="button"
                  className="nova-p4-secondary"
                  style={{ width: 'auto' }}
                  onClick={() => {
                    void saveBiller({ billerId: receipt.billerId, params: receipt.params, nickname: nickname.trim() || receipt.billerName })
                      .then(() => toast.success('Biller saved'));
                  }}
                >
                  Save
                </button>
              </div>
            </div>
          )}

          <button
            type="button"
            className="nova-p4-primary"
            style={{ marginTop: 12 }}
            onClick={() => navigate((location.state as { backTo?: string } | null)?.backTo ?? '/pay/bills')}
          >
            Done
          </button>
        </div>
      )}

      <PaymentAuthSheets
        authState={auth.authState}
        approvalLine={biller ? `Pay ${biller.name} ${formatIndianCurrency(Number(amount || 0) + fee)}` : ''}
        pinError={auth.pinError}
        pinResetKey={auth.pinResetKey}
        otpExpiry={auth.otpExpiry}
        otpSessionKey={auth.otpSessionKey}
        onCompletePin={auth.completePin}
        onVerifyOtp={auth.verifyOtp}
        onResendOtp={auth.resendOtp}
        onClose={auth.close}
      />
    </div>
  );
};

export default BillPayFlow;
